/* Tailwind CSS v2+ | Headless UI | Gatsby Link */
import React, { Fragment, useState } from "react";
import { Link } from "gatsby";
import AbtLogo from "~/images/Art_of_bicycle_trips_Final_logo2.png";
import { Popover, Transition, Dialog, Disclosure } from "@headlessui/react";
import { MenuIcon, XIcon, ChevronDownIcon } from "@heroicons/react/outline";

/* -------- data (from your original) -------- */
const asia = [
	{ name: "Bali", to: "/destinations/bali-bike-tours/" },
	{ name: "Bhutan", to: "/destinations/bhutan-cycling-tours/" },
	{ name: "Cambodia", to: "/destinations/cambodia-bike-tours/" },
	{ name: "India", to: "/destinations/india-bicycle-tours/" },
	{ name: "Japan", to: "/destinations/japan-bike-tours/" },
	{ name: "Jordan", to: "/destinations/jordan-bike-tours/" },
	{ name: "Laos", to: "/destinations/laos-bike-tours/" },
	{ name: "Mongolia", to: "/destinations/mongolia-bike-tours/" },
	{ name: "Nepal", to: "/destinations/nepal-bike-tours/" },
	{ name: "Philippines", to: "/destinations/philippines-bike-tours/" },
];
const asia2 = [
	{ name: "Sri Lanka", to: "/destinations/sri-lanka-cycling-tours/" },
	{ name: "Taiwan", to: "/destinations/taiwan-bike-tours/" },
	{ name: "Thailand", to: "/destinations/thailand-bike-tours/" },
	{ name: "Vietnam", to: "/destinations/vietnam-bike-tours/" },
];
const asiaHiking = [
	{ name: "Bhutan", to: "/destinations/bhutan-hiking-tours/" },
	{ name: "Japan", to: "/destinations/japan-hiking-tours/" },
	{ name: "Nepal", to: "/destinations/nepal-trekking-tours/" },
	{ name: "Sri Lanka", to: "/destinations/sri-lanka-hiking-tours/" },
	{ name: "Vietnam", to: "/destinations/vietnam-hiking-tours/" },
];
const asiaMulti = [
	{ name: "Bhutan", to: "/destinations/bhutan-multi-adventure-tours/" },
	{ name: "Cambodia", to: "/destinations/cambodia-multi-adventure-tours/" },
	{ name: "Laos", to: "/destinations/laos-multi-adventure-tours/" },
	{ name: "Sri Lanka", to: "/destinations/sri-lanka-multi-adventure-tours/" },
	{ name: "Thailand", to: "/destinations/thailand-multi-adventure-tours/" },
	{ name: "Vietnam", to: "/destinations/vietnam-multi-adventure-tours/" },
];
const southam = [{ name: "Chile", to: "/destinations/chile-bike-tours/" }];
const africa = [
	{ name: "Botswana", to: "/destinations/botswana-bike-tours/" },
	{ name: "Morocco", to: "/destinations/morocco-bike-tours/" },
	{ name: "South Africa", to: "/destinations/south-africa-bike-tours/" },
];
const europe = [
	{ name: "Albania", to: "/destinations/albania-bike-tours/" },
	{ name: "Austria", to: "/destinations/austria-bike-tours/" },
	{ name: "Croatia", to: "/destinations/croatia-bike-tours/" },
	{ name: "Czech Republic", to: "/destinations/czech-republic-bike-tours/" },
	{ name: "France", to: "/destinations/france-bike-tours/" },
	{ name: "Germany", to: "/destinations/germany-bike-tours/" },
	{ name: "Greece", to: "/destinations/greece-bike-tours/" },
	{ name: "Hungary", to: "/destinations/hungary-bike-tours/" },
	{ name: "Ireland", to: "/destinations/ireland-bike-tours/" },
	{ name: "Italy", to: "/destinations/italy-bike-tours/" },
];
const europe2 = [
	{ name: "Montenegro", to: "/destinations/montenegro-bike-tours/" },
	{ name: "Norway", to: "/destinations/norway-bike-tours/" },
	{ name: "Poland", to: "/destinations/poland-bike-tours/" },
	{ name: "Portugal", to: "/destinations/portugal-bike-tours/" },
	{ name: "Romania", to: "/destinations/romania-bike-tours/" },
	{ name: "Slovakia", to: "/destinations/slovakia-bike-tours/" },
	{ name: "Slovenia", to: "/destinations/slovenia-bike-tours/" },
	{ name: "Spain", to: "/destinations/spain-bike-tours/" },
];
const europeHiking = [
	{ name: "Croatia", to: "/destinations/croatia-hiking-tours/" },
	{ name: "Portugal", to: "/destinations/portugal-hiking-tours/" },
];
const specialities = [
	{ name: "Bike & Boat Tours", to: "/specialities/bike-and-boat-tours/" },
	{
		name: "Self-guided Bike Tours",
		to: "/specialities/self-guided-bike-tours/",
	},
	{ name: "Luxury Bike Tours", to: "/specialities/luxury-bike-tours/" },
	{
		name: "E-Bike Tours for Seniors",
		to: "/specialities/e-bike-tours-for-seniors/",
	},
	{ name: "Road Bike Tours", to: "/specialities/road-bike-tours/" },
	{ name: "Gravel Bike Tours", to: "/specialities/gravel-bike-tours/" },
	{ name: "Multi-sport Adventures", to: "/specialities/multi-sport-tours/" },
	{ name: "Family Bike Tours", to: "/specialities/family/" },
];

function classNames(...classes) {
	return classes.filter(Boolean).join(" ");
}

export default function Navbar() {
	const [mobileOpen, setMobileOpen] = useState(false);

	return (
		<Popover className="relative bg-white">
			{({ open }) => (
				<>
					{/* Top Bar */}
					<div className="max-w-7xl mx-auto">
						<div className="flex justify-between items-center py-2 lg:justify-start lg:space-x-10">
							<div className="flex justify-start my-4 lg:w-0 lg:flex-1">
								<Link to="/">
									<span className="sr-only">Art of Bicycle Trips</span>
									<img
										className="h-12 w-auto sm:h-14"
										src={AbtLogo}
										alt="Art of Bicycle Trips logo"
									/>
								</Link>
							</div>

							{/* Desktop CTAs — show only at lg+ to avoid crowding md */}
							<div className="hidden lg:flex items-center justify-end lg:flex-1 lg:w-0 lg:mr-6">
								<a
									href="/design-trip/"
									className="whitespace-nowrap tracking-wide font-semibold text-primary px-5 py-2 border border-primary rounded-full shadow-sm"
								>
									Design my private trip
								</a>
								<a
									href="/contact-us/"
									className="ml-4 tracking-wide whitespace-nowrap inline-flex items-center justify-center px-7 py-2 border border-transparent rounded-full shadow-sm font-semibold text-white bg-primary hover:bg-primary"
								>
									Contact us
								</a>
							</div>

							{/* Mobile hamburger button — visible up to lg-1 */}
							<div className="lg:hidden -mr-2 -my-2">
								<button
									type="button"
									onClick={() => setMobileOpen(true)}
									className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
									aria-label="Open menu"
								>
									<MenuIcon className="h-6 w-6" aria-hidden="true" />
								</button>
							</div>
						</div>

						{/* Desktop nav links & mega menus */}
						<Popover.Group as="nav" className="hidden lg:flex lg:pb-10 space-x-8">
							{/* Destinations mega menu */}
							<Popover className="relative">
								{({ open }) => (
									<>
										<Popover.Button
											className={classNames(
												open ? "text-gray-900" : "text-gray-600",
												"group bg-white rounded-md inline-flex items-center text-base font-medium hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
											)}
										>
											<span>Biking</span>
											<ChevronDownIcon
												className={classNames(
													open ? "text-gray-600" : "text-gray-400",
													"ml-2 h-5 w-5 group-hover:text-gray-500"
												)}
												aria-hidden="true"
											/>
										</Popover.Button>

										<Transition
											show={open}
											as={Fragment}
											enter="transition ease-out duration-200"
											enterFrom="opacity-0 translate-y-1"
											enterTo="opacity-100 translate-y-0"
											leave="transition ease-in duration-150"
											leaveFrom="opacity-100 translate-y-0"
											leaveTo="opacity-0 translate-y-1"
										>
											<Popover.Panel
												static
												className="absolute z-20 -ml-4 mt-3 transform px-2 w-screen max-w-3xl sm:px-0 lg:ml-0 lg:left-1/2"
											>
												<div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden">
													<div className="relative grid grid-cols-6 gap-6 bg-white px-5 py-6 sm:gap-8 sm:p-8">
														<div className="ml-4">
															<p className="mb-4">
																<a
																	href="/destinations/asia-bike-tours"
																	className="text-base font-medium text-primary"
																>
																	ASIA
																</a>
															</p>
															{asia.map((item) => (
																<a
																	key={item.name}
																	href={item.to}
																	className="-m-3 p-3 flex items-start rounded-lg hover:bg-secondary"
																>
																	<p className="text-sm text-gray-600 hover:text-gray-900">
																		{item.name}
																	</p>
																</a>
															))}
														</div>
														<div className="ml-4">
															<p className="mb-4">
																<a
																	href="/destinations/asia-bike-tours"
																	className="text-base font-medium text-primary"
																>
																	ASIA
																</a>
															</p>
															{asia2.map((item) => (
																<a
																	key={item.name}
																	href={item.to}
																	className="-m-3 p-3 flex items-start rounded-lg hover:bg-secondary"
																>
																	<p className="text-sm text-gray-600 hover:text-gray-900">
																		{item.name}
																	</p>
																</a>
															))}
														</div>
														<div className="ml-4">
															<p className="mb-4">
																<a
																	href="/destinations/europe-bike-tours"
																	className="text-base font-medium text-primary"
																>
																	EUROPE
																</a>
															</p>
															{europe.map((item) => (
																<a
																	key={item.name}
																	href={item.to}
																	className="-m-3 p-3 flex items-start rounded-lg hover:bg-secondary"
																>
																	<p className="text-sm text-gray-600 hover:text-gray-900">
																		{item.name}
																	</p>
																</a>
															))}
														</div>
														<div className="ml-4">
															<p className="mb-4">
																<a
																	href="/destinations/europe-bike-tours"
																	className="text-base font-medium text-primary"
																>
																	EUROPE
																</a>
															</p>
															{europe2.map((item) => (
																<a
																	key={item.name}
																	href={item.to}
																	className="-m-3 p-3 flex items-start rounded-lg hover:bg-secondary"
																>
																	<p className="text-sm text-gray-600 hover:text-gray-900">
																		{item.name}
																	</p>
																</a>
															))}
														</div>
														<div className="ml-4">
															<p className="mb-4">
																<a
																	href="/destinations/africa-bike-tours"
																	className="text-base font-medium text-primary"
																>
																	AFRICA
																</a>
															</p>
															{africa.map((item) => (
																<a
																	key={item.name}
																	href={item.to}
																	className="-m-3 p-3 flex items-start rounded-lg hover:bg-secondary"
																>
																	<p className="text-sm text-gray-600 hover:text-gray-900">
																		{item.name}
																	</p>
																</a>
															))}
														</div>
														<div className="ml-4">
															<p className="mb-4">
																<a
																	href="/destinations/south-america-bike-tours"
																	className="text-base font-medium text-primary"
																>
																	SOUTH AMERICA
																</a>
															</p>
															{southam.map((item) => (
																<a
																	key={item.name}
																	href={item.to}
																	className="-m-3 p-3 flex items-start rounded-lg hover:bg-secondary"
																>
																	<p className="text-sm text-gray-600 hover:text-gray-900">
																		{item.name}
																	</p>
																</a>
															))}
														</div>
													</div>
												</div>
											</Popover.Panel>
										</Transition>
									</>
								)}
							</Popover>
							{/* Hiking */}
							<Popover className="relative">
								{({ open }) => (
									<>
										<Popover.Button
											className={classNames(
												open ? "text-gray-900" : "text-gray-600",
												"group bg-white rounded-md inline-flex items-center text-base font-medium hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
											)}
										>
											<span>Hiking & Walking</span>
											<ChevronDownIcon
												className={classNames(
													open ? "text-gray-600" : "text-gray-400",
													"ml-2 h-5 w-5 group-hover:text-gray-500"
												)}
												aria-hidden="true"
											/>
										</Popover.Button>

										<Transition
											show={open}
											as={Fragment}
											enter="transition ease-out duration-200"
											enterFrom="opacity-0 translate-y-1"
											enterTo="opacity-100 translate-y-0"
											leave="transition ease-in duration-150"
											leaveFrom="opacity-100 translate-y-0"
											leaveTo="opacity-0 translate-y-1"
										>
											<Popover.Panel
												static
												className="absolute z-20 -ml-4 mt-3 transform px-2 w-screen max-w-md sm:px-0 lg:ml-0 lg:left-1/2"
											>
												<div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden">
													<div className="relative grid grid-cols-2 gap-6 bg-white px-5 py-6 sm:gap-8 sm:p-8">
														<div className="ml-4">
															<p className="mb-4">
																<a
																	href="/destinations/asia-hiking-tours"
																	className="text-base font-medium text-primary"
																>
																	ASIA
																</a>
															</p>
															{asiaHiking.map((item) => (
																<a
																	key={item.name}
																	href={item.to}
																	className="-m-3 p-3 flex items-start rounded-lg hover:bg-secondary"
																>
																	<p className="text-sm text-gray-600 hover:text-gray-900">
																		{item.name}
																	</p>
																</a>
															))}
														</div>

														<div className="ml-4">
															<p className="mb-4">
																<a
																	href="/destinations/europe-hiking-tours"
																	className="text-base font-medium text-primary"
																>
																	EUROPE
																</a>
															</p>
															{europeHiking.map((item) => (
																<a
																	key={item.name}
																	href={item.to}
																	className="-m-3 p-3 flex items-start rounded-lg hover:bg-secondary"
																>
																	<p className="text-sm text-gray-600 hover:text-gray-900">
																		{item.name}
																	</p>
																</a>
															))}
														</div>
													</div>
												</div>
											</Popover.Panel>
										</Transition>
									</>
								)}
							</Popover>
							{/* Multi-adventure */}
							<Popover className="relative">
								{({ open }) => (
									<>
										<Popover.Button
											className={classNames(
												open ? "text-gray-900" : "text-gray-600",
												"group bg-white rounded-md inline-flex items-center text-base font-medium hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
											)}
										>
											<span>Multi-Adventure</span>
											<ChevronDownIcon
												className={classNames(
													open ? "text-gray-600" : "text-gray-400",
													"ml-2 h-5 w-5 group-hover:text-gray-500"
												)}
												aria-hidden="true"
											/>
										</Popover.Button>

										<Transition
											show={open}
											as={Fragment}
											enter="transition ease-out duration-200"
											enterFrom="opacity-0 translate-y-1"
											enterTo="opacity-100 translate-y-0"
											leave="transition ease-in duration-150"
											leaveFrom="opacity-100 translate-y-0"
											leaveTo="opacity-0 translate-y-1"
										>
											<Popover.Panel
												static
												className="absolute z-20 -ml-4 mt-3 transform px-2 w-screen max-w-xs sm:px-0 lg:ml-0 lg:left-1/2"
											>
												<div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden">
													<div className="relative grid grid-cols-1 gap-6 bg-white px-5 py-6 sm:gap-8 sm:p-8">
														<div className="ml-4">
															<p className="mb-4">
																<a
																	href="/destinations/asia-multi-adventure-tours"
																	className="text-base font-medium text-primary"
																>
																	ASIA
																</a>
															</p>
															{asiaMulti.map((item) => (
																<a
																	key={item.name}
																	href={item.to}
																	className="-m-3 p-3 flex items-start rounded-lg hover:bg-secondary"
																>
																	<p className="text-sm text-gray-600 hover:text-gray-900">
																		{item.name}
																	</p>
																</a>
															))}
														</div>
													</div>
												</div>
											</Popover.Panel>
										</Transition>
									</>
								)}
							</Popover>

							{/* Collections mega menu */}
							{/* <Popover className="relative">
								{({ open }) => (
									<>
										<Popover.Button
											className={classNames(
												open ? "text-gray-900" : "text-gray-600",
												"group bg-white rounded-md inline-flex items-center text-base font-medium hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
											)}
										>
											<span>Collections</span>
											<ChevronDownIcon
												className={classNames(
													open ? "text-gray-600" : "text-gray-400",
													"ml-2 h-5 w-5 group-hover:text-gray-500"
												)}
												aria-hidden="true"
											/>
										</Popover.Button>

										<Transition
											show={open}
											as={Fragment}
											enter="transition ease-out duration-200"
											enterFrom="opacity-0 translate-y-1"
											enterTo="opacity-100 translate-y-0"
											leave="transition ease-in duration-150"
											leaveFrom="opacity-100 translate-y-0"
											leaveTo="opacity-0 translate-y-1"
										>
											<Popover.Panel
												static
												className="absolute z-20 -ml-4 mt-3 transform px-2 w-screen max-w-xs sm:px-0 lg:ml-0 lg:left-1/2"
											>
												<div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden">
													<div className="relative grid gap-6 bg-white px-5 py-6 sm:gap-6 sm:p-8">
														{specialities.map((item) => (
															<a
																key={item.name}
																href={item.to}
																className="-m-3 p-3 flex items-start rounded-lg hover:bg-secondary"
															>
																<p className="text-sm text-gray-600 hover:text-gray-900">
																	{item.name}
																</p>
															</a>
														))}
													</div>
												</div>
											</Popover.Panel>
										</Transition>
									</>
								)}
							</Popover> */}

							{/* Simple links */}
							{/* <Link
								to="/guaranteed-bike-tours"
								className="text-base font-medium text-gray-600 hover:text-gray-900"
							>
								Guaranteed Trips
							</Link> */}
							<Link
								to="/about-us/reviews"
								className="text-base font-medium text-gray-600 hover:text-gray-900"
							>
								Reviews
							</Link>
							<Link
								to="/about-us/our-company"
								className="text-base font-medium text-gray-600 hover:text-gray-900"
							>
								About Us
							</Link>
						</Popover.Group>
					</div>

					{/* MOBILE DRAWER */}
					<Transition show={mobileOpen} as={Fragment}>
						<Dialog
							as="div"
							className="fixed inset-0 z-50 lg:hidden"
							open={mobileOpen}
							onClose={setMobileOpen}
						>
							{/* Backdrop */}
							<Transition.Child
								as={Fragment}
								enter="ease-out duration-200"
								enterFrom="opacity-0"
								enterTo="opacity-100"
								leave="ease-in duration-150"
								leaveFrom="opacity-100"
								leaveTo="opacity-0"
							>
								<div className="fixed inset-0 bg-black bg-opacity-30" />
							</Transition.Child>

							{/* Panel */}
							<div className="fixed inset-y-0 right-0 max-w-full flex">
								<Transition.Child
									as={Fragment}
									enter="transform transition ease-in-out duration-300"
									enterFrom="translate-x-full"
									enterTo="translate-x-0"
									leave="transform transition ease-in-out duration-200"
									leaveFrom="translate-x-0"
									leaveTo="translate-x-full"
								>
									<Dialog.Panel className="w-screen max-w-md bg-white shadow-xl focus:outline-none">
										{/* Header inside drawer */}
										<div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
											<Link
												to="/"
												onClick={() => setMobileOpen(false)}
												className="flex items-center"
											>
												<img src={AbtLogo} alt="ABT" className="h-10 w-auto" />
											</Link>
											<button
												type="button"
												onClick={() => setMobileOpen(false)}
												className="rounded-md p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary"
												aria-label="Close menu"
											>
												<XIcon className="h-6 w-6" />
											</button>
										</div>

										{/* CTAs */}
										<div className="px-5 pt-4">
											<Link
												to="/contact-us"
												onClick={() => setMobileOpen(false)}
												className="w-full flex items-center justify-center mb-3 px-4 py-2 border border-transparent rounded-xl shadow-sm text-base font-semibold text-white bg-primary"
											>
												Contact us
											</Link>
											<Link
												to="/design-trip"
												onClick={() => setMobileOpen(false)}
												className="w-full flex items-center justify-center px-4 py-2 border border-primary rounded-xl shadow-sm text-base font-semibold text-primary"
											>
												Design my private trip
											</Link>
										</div>

										{/* Accordions */}
										<div className="px-2 py-4 overflow-y-auto h-full">
											<nav className="space-y-2">
												<Disclosure>
													{({ open }) => (
														<div className="rounded-2xl border border-gray-200">
															<Disclosure.Button className="w-full flex items-center justify-between px-4 py-3 text-left">
																<span className="text-base font-medium text-gray-900">
																	Biking
																</span>
																<ChevronDownIcon
																	className={classNames(
																		"h-5 w-5 transform transition",
																		open && "rotate-180"
																	)}
																/>
															</Disclosure.Button>
															<Disclosure.Panel as="div" className="pb-3">
																{/* Asia */}
																<div className="px-4">
																	<p className="text-sm font-semibold text-primary mb-2">Asia</p>
																	<div className="grid grid-cols-2 gap-1">
																		{asia.concat(asia2).map((item) => (
																			<a
																				key={item.name}
																				href={item.to}
																				className="text-sm py-2 rounded-lg px-2 hover:bg-gray-50 block"
																				onClick={() => setMobileOpen(false)}
																			>
																				{item.name}
																			</a>
																		))}
																	</div>
																</div>
																{/* Europe */}
																<div className="px-4 mt-4">
																	<p className="text-sm font-semibold text-primary mb-2">
																		Europe
																	</p>
																	<div className="grid grid-cols-2 gap-1">
																		{europe.concat(europe2).map((item) => (
																			<a
																				key={item.name}
																				href={item.to}
																				className="text-sm py-2 rounded-lg px-2 hover:bg-gray-50 block"
																				onClick={() => setMobileOpen(false)}
																			>
																				{item.name}
																			</a>
																		))}
																	</div>
																</div>
																{/* Africa & South America */}
																<div className="px-4 mt-4 grid grid-cols-2 gap-4">
																	<div>
																		<p className="text-sm font-semibold text-primary mb-2">
																			Africa
																		</p>
																		{africa.map((item) => (
																			<a
																				key={item.name}
																				href={item.to}
																				className="block text-sm py-2 rounded-lg px-2 hover:bg-gray-50"
																				onClick={() => setMobileOpen(false)}
																			>
																				{item.name}
																			</a>
																		))}
																	</div>
																	<div>
																		<p className="text-sm font-semibold text-primary mb-2">
																			South America
																		</p>
																		{southam.map((item) => (
																			<a
																				key={item.name}
																				href={item.to}
																				className="block text-sm py-2 rounded-lg px-2 hover:bg-gray-50"
																				onClick={() => setMobileOpen(false)}
																			>
																				{item.name}
																			</a>
																		))}
																	</div>
																</div>
															</Disclosure.Panel>
														</div>
													)}
												</Disclosure>
												{/* Hiking */}
												<Disclosure>
													{({ open }) => (
														<div className="rounded-2xl border border-gray-200">
															<Disclosure.Button className="w-full flex items-center justify-between px-4 py-3 text-left">
																<span className="text-base font-medium text-gray-900">
																	Hiking & Walking
																</span>
																<ChevronDownIcon
																	className={classNames(
																		"h-5 w-5 transform transition",
																		open && "rotate-180"
																	)}
																/>
															</Disclosure.Button>
															<Disclosure.Panel as="div" className="pb-3">
																{/* Asia */}
																<div className="px-4">
																	<p className="text-sm font-semibold text-primary mb-2">Asia</p>
																	<div className="grid grid-cols-2 gap-1">
																		{asiaHiking.map((item) => (
																			<a
																				key={item.name}
																				href={item.to}
																				className="text-sm py-2 rounded-lg px-2 hover:bg-gray-50 block"
																				onClick={() => setMobileOpen(false)}
																			>
																				{item.name}
																			</a>
																		))}
																	</div>
																</div>
																{/* Europe */}
																<div className="px-4 mt-4">
																	<p className="text-sm font-semibold text-primary mb-2">
																		Europe
																	</p>
																	<div className="grid grid-cols-2 gap-1">
																		{europeHiking.map((item) => (
																			<a
																				key={item.name}
																				href={item.to}
																				className="text-sm py-2 rounded-lg px-2 hover:bg-gray-50 block"
																				onClick={() => setMobileOpen(false)}
																			>
																				{item.name}
																			</a>
																		))}
																	</div>
																</div>
																{/* Africa & South America */}
																{/* <div className="px-4 mt-4 grid grid-cols-2 gap-4">
																	<div>
																		<p className="text-sm font-semibold text-primary mb-2">
																			Africa
																		</p>
																		{africa.map((item) => (
																			<a
																				key={item.name}
																				href={item.to}
																				className="block text-sm py-2 rounded-lg px-2 hover:bg-gray-50"
																				onClick={() => setMobileOpen(false)}
																			>
																				{item.name}
																			</a>
																		))}
																	</div>
																	<div>
																		<p className="text-sm font-semibold text-primary mb-2">
																			South America
																		</p>
																		{southam.map((item) => (
																			<a
																				key={item.name}
																				href={item.to}
																				className="block text-sm py-2 rounded-lg px-2 hover:bg-gray-50"
																				onClick={() => setMobileOpen(false)}
																			>
																				{item.name}
																			</a>
																		))}
																	</div>
																</div> */}
															</Disclosure.Panel>
														</div>
													)}
												</Disclosure>
												{/* Multi-Adventure */}
												<Disclosure>
													{({ open }) => (
														<div className="rounded-2xl border border-gray-200">
															<Disclosure.Button className="w-full flex items-center justify-between px-4 py-3 text-left">
																<span className="text-base font-medium text-gray-900">
																	Multi-Adventure
																</span>
																<ChevronDownIcon
																	className={classNames(
																		"h-5 w-5 transform transition",
																		open && "rotate-180"
																	)}
																/>
															</Disclosure.Button>
															<Disclosure.Panel as="div" className="pb-3">
																<div className="px-4">
																	<p className="text-sm font-semibold text-primary mb-2">Asia</p>
																	<div className="grid grid-cols-2 gap-1">
																		{asiaMulti.map((item) => (
																			<a
																				key={item.name}
																				href={item.to}
																				className="text-sm py-2 rounded-lg px-2 hover:bg-gray-50 block"
																				onClick={() => setMobileOpen(false)}
																			>
																				{item.name}
																			</a>
																		))}
																	</div>
																</div>
															</Disclosure.Panel>
														</div>
													)}
												</Disclosure>
												{/* <Disclosure>
													{({ open }) => (
														<div className="rounded-2xl border border-gray-200">
															<Disclosure.Button className="w-full flex items-center justify-between px-4 py-3 text-left">
																<span className="text-base font-medium text-gray-900">
																	Collections
																</span>
																<ChevronDownIcon
																	className={classNames(
																		"h-5 w-5 transform transition",
																		open && "rotate-180"
																	)}
																/>
															</Disclosure.Button>
															<Disclosure.Panel as="div" className="pb-2 px-2">
																<div className="grid grid-cols-1 gap-1">
																	{specialities.map((item) => (
																		<a
																			key={item.name}
																			href={item.to}
																			className="block text-sm py-2 rounded-lg px-2 hover:bg-gray-50"
																			onClick={() => setMobileOpen(false)}
																		>
																			{item.name}
																		</a>
																	))}
																</div>
															</Disclosure.Panel>
														</div>
													)}
												</Disclosure> */}

												{/* Static single links with border & rounded style */}
												<div className="space-y-2">
													{/* <Link
														to="/guaranteed-bike-tours"
														onClick={() => setMobileOpen(false)}
														className="block px-4 py-3 border border-gray-200 rounded-2xl text-base font-medium text-gray-900 hover:bg-gray-50 hover:text-primary"
													>
														Guaranteed Trips
													</Link> */}
													<Link
														to="/about-us/reviews"
														onClick={() => setMobileOpen(false)}
														className="block px-4 py-3 border border-gray-200 rounded-2xl text-base font-medium text-gray-900 hover:bg-gray-50 hover:text-primary"
													>
														Reviews
													</Link>
													<Link
														to="/about-us/our-company"
														onClick={() => setMobileOpen(false)}
														className="block px-4 py-3 border border-gray-200 rounded-2xl text-base font-medium text-gray-900 hover:bg-gray-50 hover:text-primary"
													>
														About Us
													</Link>
												</div>
											</nav>
										</div>

										<div className="h-6" />
									</Dialog.Panel>
								</Transition.Child>
							</div>
						</Dialog>
					</Transition>
				</>
			)}
		</Popover>
	);
}
